import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { AccountBalanceWallet as WalletIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useUserCreditBalance } from '../../hooks/useUserData';
import { useActCreditBalance } from '../../hooks/useActData';
import { useAuthProvider } from '../../utils/auth';

const CreditStatusIndicator = ({ showIcon = true, showLabel = true, size = 'medium' }) => {
  const { isActListeningMode, currentActId } = useAuthProvider();
  
  // Use act credit balance if in act listening mode, otherwise use user credit balance
  const { data: userCreditBalance, isLoading: userLoading } = useUserCreditBalance({
    enabled: !isActListeningMode
  });
  const { data: actCreditBalance, isLoading: actLoading } = useActCreditBalance(currentActId, {
    enabled: isActListeningMode && !!currentActId
  });
  
  const creditBalance = isActListeningMode ? actCreditBalance : userCreditBalance;
  const isLoading = isActListeningMode ? actLoading : userLoading;

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showIcon && <WalletIcon />}
        {showLabel && <Typography variant="body2">Loading...</Typography>}
      </Box>
    );
  }

  const getStatusColor = (balance) => {
    if (balance <= 0.50) return 'error';
    if (balance <= 2.00) return 'warning';
    return 'success';
  };

  const getStatusText = (balance) => {
    if (balance <= 0.50) return 'Critical';
    if (balance <= 2.00) return 'Low';
    return 'Good';
  };

  const getSizeProps = (size) => {
    switch (size) {
      case 'small':
        return { variant: 'caption', iconSize: 16 };
      case 'large':
        return { variant: 'body1', iconSize: 24 };
      default:
        return { variant: 'body2', iconSize: 20 };
    }
  };

  const sizeProps = getSizeProps(size);
  const statusColor = getStatusColor(creditBalance);
  const statusText = getStatusText(creditBalance);

  return (
    <Tooltip title={`${isActListeningMode ? 'Artist' : 'User'} Credit Status: ${statusText} (${formatBalance(creditBalance)})`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showIcon && (
          <Box sx={{ position: 'relative' }}>
            <WalletIcon sx={{ fontSize: sizeProps.iconSize }} />
            {statusColor === 'error' && (
              <WarningIcon 
                sx={{ 
                  position: 'absolute', 
                  top: -2, 
                  right: -2, 
                  fontSize: 12, 
                  color: 'error.main' 
                }} 
              />
            )}
          </Box>
        )}
        {showLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={sizeProps.variant} sx={{ fontWeight: 'bold' }}>
              {formatBalance(creditBalance)}
            </Typography>
            <Chip 
              label={statusText} 
              color={statusColor} 
              size="small" 
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default CreditStatusIndicator;
